import { hasValue } from '@repo/utils';
import { action, computed, flow, makeObservable, observable, reaction, type IReactionDisposer } from 'mobx';

import { isLocalNewTrajectoryId, type FactTrajectoryEntity } from '../../service/entities';
import type { TrajectoryService } from '../../service/trajectory-service';
import type { TrajectoryUiStore } from '../../trajectory-ui.store';
import type { ITrajectoryNotificationsService, ITrajectoryPreferencesService } from '../../types/host-services';
import type { TFactTrajectoryCreateDefaults } from '../../types/trajectory';
import type { TTrajectoryValue, TAzimuthReference } from '../../types/trajectory-measure';
import { getApiErrorMessage } from '../../utils/errors';
import { isAzimuthReference } from '../../utils/is-azimuth-reference';
import type { TrajectoryUomOptionsStore } from '../../utils/trajectory-uom-options';

function normalizeTitle(value: string | undefined | null): string {
  return (value ?? '').trim();
}

function readAngleValue(measure: TTrajectoryValue | undefined | null): number | null {
  if (!measure || typeof measure.value !== 'number' || Number.isNaN(measure.value)) {
    return null;
  }

  return measure.value;
}

function readDatumUuid(datum: FactTrajectoryEntity['data']['defaultMdDatum'] | undefined): string | null {
  const base = datum?.baseReferencePoint;

  if (!base || typeof base !== 'object' || !('uuid' in base)) {
    return null;
  }

  return typeof base.uuid === 'string' && base.uuid.length > 0 ? base.uuid : null;
}

export class TrajectoryListSidebarStore {
  @observable previewTrajectoryId: number | null = null;
  @observable isEditing = false;

  readonly uomOptions: TrajectoryUomOptionsStore;

  private readonly trajectoryService: TrajectoryService;
  private readonly uiStore: TrajectoryUiStore;
  private readonly notifications: ITrajectoryNotificationsService;
  private readonly preferences: ITrajectoryPreferencesService | null;
  private disposers: IReactionDisposer[] = [];

  constructor(
    trajectoryService: TrajectoryService,
    uiStore: TrajectoryUiStore,
    notifications: ITrajectoryNotificationsService,
    uomOptions: TrajectoryUomOptionsStore,
    preferences: ITrajectoryPreferencesService | null = null,
  ) {
    this.trajectoryService = trajectoryService;
    this.uiStore = uiStore;
    this.notifications = notifications;
    this.uomOptions = uomOptions;
    this.preferences = preferences;
    makeObservable(this);
  }

  @action.bound
  setIsEditing(isEditing: boolean): void {
    if (this.isEditing === isEditing) {
      return;
    }

    this.isEditing = isEditing;

    if (!isEditing) {
      this.discardUncommittedPreview();
    }
  }

  @computed
  get trajectories(): FactTrajectoryEntity[] {
    return this.trajectoryService.wellboreTrajectories;
  }

  /** Local draft that has not been persisted yet (negative temp id). */
  @computed
  get isNewDraft(): boolean {
    return isLocalNewTrajectoryId(this.previewTrajectory?.id);
  }

  @computed
  get isLoading(): boolean {
    return this.trajectoryService.isWellboreTrajectoriesLoading;
  }

  @computed
  get isSaving(): boolean {
    return this.trajectoryService.isSaving;
  }

  @computed
  get previewTrajectory(): FactTrajectoryEntity | null {
    return this.trajectories.find((item) => item.id === this.previewTrajectoryId) ?? null;
  }

  /** List lock / cancel / apply dirty state. New local trajectory is always dirty. */
  @computed
  get hasChanges(): boolean {
    return this.previewTrajectory?.hasChanges ?? false;
  }

  @computed
  get isListLocked(): boolean {
    return this.isEditing && (this.hasChanges || this.isSaving);
  }

  @computed
  get canCancel(): boolean {
    return this.isEditing && this.hasChanges;
  }

  @computed
  get canApply(): boolean {
    if (!this.isEditing || !this.previewTrajectory || this.isSaving) {
      return false;
    }

    return this.hasChanges;
  }

  @computed
  get canAddTrajectory(): boolean {
    return this.isEditing && !this.isListLocked && hasValue(this.trajectoryService.selectedWellboreId);
  }

  canDeleteTrajectory(trajectoryId: number): boolean {
    if (!this.isEditing || this.isSaving) {
      return false;
    }

    if (this.isListLocked && trajectoryId !== this.previewTrajectoryId) {
      return false;
    }

    return true;
  }

  @computed
  get aziRefOptions(): { label: string; value: TAzimuthReference }[] {
    return [
      { value: 'MAGNETIC', label: 'ListSidebar.azimuthReference.MAGNETIC' },
      { value: 'TRUE', label: 'ListSidebar.azimuthReference.TRUE' },
      { value: 'GRID', label: 'ListSidebar.azimuthReference.GRID' },
    ];
  }

  @computed
  get defaultAzimuthReference(): TAzimuthReference {
    return this.preferences?.getDefaultAzimuthReference() ?? 'TRUE';
  }

  @computed
  get parentTrajectoryCandidates(): { id: number; title: string }[] {
    return [...this.trajectoryService.parentWellboreTrajectories];
  }

  @computed
  get hasParentWellbore(): boolean {
    return this.trajectoryService.hasParentWellbore;
  }

  getParentTrajectoryTitle(parentTrajectoryId: number | null | undefined): string | null {
    if (!hasValue(parentTrajectoryId)) {
      return null;
    }

    const fromParent = this.trajectoryService.parentWellboreTrajectories.find((item) => item.id === parentTrajectoryId);

    if (fromParent) {
      return normalizeTitle(fromParent.title) || null;
    }

    return null;
  }

  @computed
  get wellboreReferencePoints() {
    const wellboreId = this.trajectoryService.selectedWellboreId;
    const wellbore = this.trajectoryService.wellbores?.find((item) => item.id === wellboreId);

    return wellbore?.referencePoints ?? [];
  }

  @action.bound
  setPreviewTrajectoryId(trajectoryId: number | null): void {
    if (!hasValue(trajectoryId)) {
      return;
    }

    if (this.isListLocked && trajectoryId !== this.previewTrajectoryId) {
      return;
    }

    this.previewTrajectoryId = trajectoryId;
  }

  @action.bound
  setDraftTitle(title: string): void {
    if (this.isSaving) {
      return;
    }

    this.previewTrajectory?.updateData({ citation: { title } });
  }

  @action.bound
  setDraftDescription(description: string): void {
    if (this.isSaving) {
      return;
    }

    this.previewTrajectory?.updateData({ citation: { description } });
  }

  @action.bound
  setOrganizationId(value: number | null): void {
    if (this.isSaving) {
      return;
    }

    this.previewTrajectory?.updateData({ organizationId: value ?? undefined });
  }

  @action.bound
  setAziRef(value: string | null): void {
    if (this.isSaving) {
      return;
    }

    if (!isAzimuthReference(value)) {
      this.previewTrajectory?.updateData({ aziRef: undefined });
      return;
    }

    this.previewTrajectory?.updateData({ aziRef: value });
  }

  @action.bound
  setGridConUsed(value: number | null): void {
    this.setAngleField('gridConUsed', value);
  }

  @action.bound
  setMagDeclUsed(value: number | null): void {
    this.setAngleField('magDeclUsed', value);
  }

  @action.bound
  setAziVertSect(value: number | null): void {
    this.setAngleField('aziVertSect', value);
  }

  @action.bound
  setDepthUomId(value: number | null): void {
    if (this.isSaving) {
      return;
    }

    this.previewTrajectory?.updateData({ depthUomId: value ?? undefined });
  }

  @action.bound
  setSeverityUomId(value: number | null): void {
    if (this.isSaving) {
      return;
    }

    this.previewTrajectory?.updateData({ severityUomId: value ?? undefined });
  }

  @action.bound
  setCoordinatesUomId(value: number | null): void {
    if (this.isSaving) {
      return;
    }

    this.previewTrajectory?.updateData({ coordinatesUomId: value ?? undefined });
  }

  @action.bound
  setDefaultMdDatum(uuid: string | null): void {
    this.setDefaultDatum('defaultMdDatum', uuid);
  }

  @action.bound
  setDefaultTvdDatum(uuid: string | null): void {
    this.setDefaultDatum('defaultTvdDatum', uuid);
  }

  getDefaultMdDatumUuid(): string | null {
    return readDatumUuid(this.previewTrajectory?.data.defaultMdDatum);
  }

  getDefaultTvdDatumUuid(): string | null {
    return readDatumUuid(this.previewTrajectory?.data.defaultTvdDatum);
  }

  @action.bound
  setActive(isActive: boolean): void {
    if (this.isSaving) {
      return;
    }

    this.previewTrajectory?.updateData({ active: isActive });
  }

  @action.bound
  setFinalTraj(isFinal: boolean): void {
    if (this.isSaving) {
      return;
    }

    this.previewTrajectory?.updateData({ finalTraj: isFinal });
  }

  @action.bound
  setParentTrajectoryId(parentTrajectoryId: number | null): void {
    if (this.isSaving || !this.hasParentWellbore) {
      return;
    }

    this.previewTrajectory?.updateData({ parentTrajectoryId: parentTrajectoryId ?? undefined });
  }

  @action.bound
  setMdTieOn(value: number | null): void {
    if (this.isSaving || !this.hasParentWellbore) {
      return;
    }

    if (value == null || Number.isNaN(value)) {
      this.previewTrajectory?.updateData({ mdTieOn: undefined });
      return;
    }

    const previous = this.previewTrajectory?.data.mdTieOn;

    this.previewTrajectory?.updateData({
      mdTieOn: {
        ...previous,
        measuredDepth: {
          value,
          uom: previous?.measuredDepth?.uom,
        },
      },
    });
  }

  @flow.bound
  async *addLocalTrajectory() {
    if (!this.canAddTrajectory) {
      return;
    }

    const wellboreId = this.trajectoryService.selectedWellboreId;

    if (!hasValue(wellboreId)) {
      return;
    }

    const entity = this.trajectoryService.createLocalTrajectory();

    if (!entity) {
      return;
    }

    this.previewTrajectoryId = entity.id;

    if (this.trajectoryService.planOrFact !== 'fact') {
      this.applyMissingDefaultsAfterCreate(entity);
      return;
    }

    try {
      const defaults: TFactTrajectoryCreateDefaults | undefined =
        yield this.trajectoryService.fetchFactTrajectoryCreateDefaults(wellboreId);
      yield;

      if (defaults && this.previewTrajectoryId === entity.id) {
        this.trajectoryService.applyFactTrajectoryCreateDefaults(entity, defaults);
      }
    } catch (e) {
      yield;
      console.error(e);
      this.notifications.showErrorMessage(getApiErrorMessage(e));
    }

    if (this.previewTrajectoryId === entity.id) {
      this.applyMissingDefaultsAfterCreate(entity);
    }
  }

  @action.bound
  close(): void {
    if (!this.isEditing) {
      this.previewTrajectoryId = this.trajectoryService.selectedTrajectoryId;
    }

    this.uiStore.setTrajectoryListOpen(false);
  }

  @action.bound
  cancelChanges(): void {
    if (!this.canCancel || this.isSaving) {
      return;
    }

    const entity = this.previewTrajectory;

    if (!entity) {
      return;
    }

    if (this.isNewDraft) {
      this.trajectoryService.removeWellboreTrajectory(entity.id);

      const first = this.trajectories[0] ?? null;
      this.previewTrajectoryId = first?.id ?? null;

      if (first) {
        this.trajectoryService.setSelection({ trajectoryId: first.id });
      }

      return;
    }

    entity.resetData();
  }

  @flow.bound
  async *apply() {
    if (!this.canApply || !this.previewTrajectory) {
      return;
    }

    try {
      const saved: FactTrajectoryEntity = yield this.trajectoryService.upsertFactTrajectory(this.previewTrajectory);
      yield;

      this.previewTrajectoryId = saved.id;
    } catch (e) {
      yield;
      console.error(e);
      this.notifications.showErrorMessage(getApiErrorMessage(e));
    }
  }

  @flow.bound
  async *deleteTrajectory(trajectoryId: number) {
    if (!this.canDeleteTrajectory(trajectoryId)) {
      return;
    }

    const wasPreview = this.previewTrajectoryId === trajectoryId;

    try {
      yield this.trajectoryService.deleteFactTrajectory(trajectoryId);
      yield;

      if (wasPreview) {
        this.previewTrajectoryId = this.trajectoryService.selectedTrajectoryId;
      }
    } catch (e) {
      yield;
      console.error(e);
      this.notifications.showErrorMessage(getApiErrorMessage(e));
    }
  }

  getAziRefLabel(value: string | null | undefined): string {
    if (!isAzimuthReference(value)) {
      return '—';
    }

    return this.aziRefOptions.find((option) => option.value === value)?.label ?? value;
  }

  getAngleFieldValue(field: 'gridConUsed' | 'magDeclUsed' | 'aziVertSect'): number | null {
    const data = this.previewTrajectory?.data;

    if (!data) {
      return null;
    }

    return readAngleValue(data[field]);
  }

  getMdTieOnValue(): number | null {
    const value = this.previewTrajectory?.data.mdTieOn?.measuredDepth.value;

    if (typeof value !== 'number' || Number.isNaN(value)) {
      return null;
    }

    return value;
  }

  getUomLabel(uomId: number | null | undefined): string {
    if (!hasValue(uomId)) {
      return '—';
    }

    return this.uomOptions.getUomLabel(uomId) ?? String(uomId);
  }

  formatAngleField(measure: TTrajectoryValue | undefined | null): string {
    const value = readAngleValue(measure);

    if (value == null) {
      return '—';
    }

    return `${value}°`;
  }

  resolveDepthUomLabel(data: FactTrajectoryEntity['data']): string {
    if (hasValue(data.depthUomId)) {
      return this.getUomLabel(data.depthUomId);
    }

    return data.mdTieOn?.measuredDepth.uom || data.defaultMdDatum?.md?.uom || '—';
  }

  resolveIntensityUomLabel(data: FactTrajectoryEntity['data']): string {
    if (hasValue(data.severityUomId)) {
      return this.getUomLabel(data.severityUomId);
    }

    return this.resolveDepthUomLabel(data);
  }

  resolveCoordinatesUomLabel(data: FactTrajectoryEntity['data']): string {
    if (hasValue(data.coordinatesUomId)) {
      return this.getUomLabel(data.coordinatesUomId);
    }

    return '°';
  }

  resolveOrganizationLabel(data: FactTrajectoryEntity['data']): string {
    if (hasValue(data.organizationId)) {
      return this.uomOptions.getOrganizationLabel(data.organizationId) ?? String(data.organizationId);
    }

    const originator = data.citation.originator?.trim();

    if (originator) {
      return originator;
    }

    return '—';
  }

  @action.bound
  private setAngleField(field: 'gridConUsed' | 'magDeclUsed' | 'aziVertSect', value: number | null): void {
    if (this.isSaving) {
      return;
    }

    if (value == null || Number.isNaN(value)) {
      this.previewTrajectory?.updateData({ [field]: undefined });
      return;
    }

    this.previewTrajectory?.updateData({
      [field]: { value },
    });
  }

  @action.bound
  private setDefaultDatum(field: 'defaultMdDatum' | 'defaultTvdDatum', uuid: string | null): void {
    if (this.isSaving) {
      return;
    }

    if (!hasValue(uuid)) {
      this.previewTrajectory?.updateData({ [field]: undefined });
      return;
    }

    this.previewTrajectory?.updateData({
      [field]: {
        baseReferencePoint: {
          uuid,
          qualifiedType: 'ReferencePointInACrs',
        },
      },
    });
  }

  @action.bound
  private applyMissingDefaultsAfterCreate(entity: FactTrajectoryEntity): void {
    const patch = this.buildMissingDefaultsPatch(entity.data);

    if (Object.keys(patch).length === 0) {
      return;
    }

    entity.updateData(patch);
  }

  private buildMissingDefaultsPatch(data: FactTrajectoryEntity['data']): Partial<FactTrajectoryEntity['data']> {
    const patch: Partial<FactTrajectoryEntity['data']> = {};

    if (!data.aziRef) {
      patch.aziRef = this.defaultAzimuthReference;
    }

    if (!hasValue(data.depthUomId) && hasValue(this.uomOptions.defaultDepthUomId)) {
      patch.depthUomId = this.uomOptions.defaultDepthUomId;
    }

    Object.assign(patch, this.buildParentLinkDefaultFields(true, data));

    return patch;
  }

  private buildMdTieOnFromKickoff(): FactTrajectoryEntity['data']['mdTieOn'] | undefined {
    const kickoffDepth = this.trajectoryService.selectedWellbore?.mdKickoff?.measuredDepth;

    if (typeof kickoffDepth !== 'number' || Number.isNaN(kickoffDepth)) {
      return undefined;
    }

    return {
      measuredDepth: {
        value: kickoffDepth,
      },
    };
  }

  /** Defaults for a new trajectory. When `onlyFillEmpty`, skip fields that already have values. */
  private buildParentLinkDefaultFields(
    onlyFillEmpty: boolean,
    data: FactTrajectoryEntity['data'] | undefined = this.previewTrajectory?.data,
  ): Partial<FactTrajectoryEntity['data']> {
    if (!this.hasParentWellbore || !data) {
      return {};
    }

    const parentTrajectoryId = this.trajectoryService.defaultParentTrajectoryId;

    if (!hasValue(parentTrajectoryId)) {
      return {};
    }

    const patch: Partial<FactTrajectoryEntity['data']> = {};

    if (!onlyFillEmpty || !hasValue(data.parentTrajectoryId)) {
      patch.parentTrajectoryId = parentTrajectoryId;
    }

    if (!onlyFillEmpty || !hasValue(data.mdTieOn)) {
      const mdTieOn = this.buildMdTieOnFromKickoff();

      if (mdTieOn) {
        patch.mdTieOn = mdTieOn;
      }
    }

    return patch;
  }

  @action.bound
  private applyParentLinkDefaultsIfNeeded(): void {
    if (!this.isNewDraft || !this.hasParentWellbore || !this.previewTrajectory || this.isSaving) {
      return;
    }

    if (this.trajectoryService.isParentWellboreTrajectoriesLoading) {
      return;
    }

    const patch = this.buildParentLinkDefaultFields(true);

    if (Object.keys(patch).length === 0) {
      return;
    }

    this.previewTrajectory.updateData(patch);
  }

  @action.bound
  private discardUncommittedPreview(): void {
    const entity = this.previewTrajectory;

    if (!entity?.hasChanges) {
      return;
    }

    if (isLocalNewTrajectoryId(entity.id)) {
      this.trajectoryService.removeWellboreTrajectory(entity.id);
      this.previewTrajectoryId = this.trajectoryService.selectedTrajectoryId;
      return;
    }

    entity.resetData();
  }

  init = (): VoidFunction => {
    void this.uomOptions.load();

    this.disposers.push(
      reaction(
        () => ({
          open: this.uiStore.isTrajectoryListOpen,
          wellboreId: this.trajectoryService.selectedWellboreId,
          selectedId: this.trajectoryService.selectedTrajectoryId,
          isEditing: this.isEditing,
        }),
        ({ open, selectedId, isEditing }) => {
          if (!open) {
            return;
          }

          if (this.isListLocked) {
            return;
          }

          if (this.isNewDraft) {
            return;
          }

          if (!hasValue(selectedId)) {
            if (!isEditing) {
              this.previewTrajectoryId = null;
            }

            return;
          }

          this.previewTrajectoryId = selectedId;
        },
        { fireImmediately: true },
      ),
      reaction(
        () => ({
          isEditing: this.isEditing,
          hasParent: this.hasParentWellbore,
          isParentCatalogLoading: this.trajectoryService.isParentWellboreTrajectoriesLoading,
          parentTrajectoryId: this.previewTrajectory?.data.parentTrajectoryId ?? null,
          candidateIds: this.parentTrajectoryCandidates.map((item) => item.id),
          hasMdTieOn: hasValue(this.previewTrajectory?.data.mdTieOn),
        }),
        ({ isEditing, hasParent, isParentCatalogLoading, parentTrajectoryId, candidateIds, hasMdTieOn }) => {
          if (!isEditing || !this.previewTrajectory || this.isSaving) {
            return;
          }

          if (!hasParent) {
            if (hasValue(parentTrajectoryId) || hasMdTieOn) {
              this.previewTrajectory.updateData({
                parentTrajectoryId: undefined,
                mdTieOn: undefined,
              });
            }

            return;
          }

          if (isParentCatalogLoading) {
            return;
          }

          if (hasValue(parentTrajectoryId) && !candidateIds.includes(parentTrajectoryId)) {
            const actualTrajId = this.previewTrajectory?.data.actualTrajId;

            if (hasValue(actualTrajId) && parentTrajectoryId === actualTrajId) {
              return;
            }

            this.previewTrajectory.updateData({
              parentTrajectoryId: undefined,
              mdTieOn: undefined,
            });
          }
        },
      ),
      reaction(
        () => ({
          isLoading: this.trajectoryService.isParentWellboreTrajectoriesLoading,
          isNewDraft: this.isNewDraft,
        }),
        ({ isLoading, isNewDraft }, previous) => {
          if (isLoading || previous?.isLoading !== true || !isNewDraft) {
            return;
          }

          this.applyParentLinkDefaultsIfNeeded();
        },
      ),
    );

    return () => {
      this.dispose();
    };
  };

  @action.bound
  dispose(): void {
    this.disposers.forEach((disposeFn) => disposeFn());
    this.disposers = [];
    this.previewTrajectoryId = null;
    this.isEditing = false;
  }
}
